import { FORM_ERROR } from 'final-form';
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  nanoid,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { showNotification, showError } from '@vk-hr-tek/core/notifications';

import { ThunkExtra, ThunkValuesAndActions } from '@app/store';
import {
  CustomDropdownItem,
  CustomLinkItem,
  GetSideMenuSettingsResponse,
  SideMenuService,
  SystemDropdownItem,
  SystemLinkItem,
  UpdateSideMenuSettingsRequest,
} from '@app/gen/side_menu';
import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import { Entity } from '@app/types';

import { getSideMenu } from '../../../../layout/slice';
import { SettingsWithRootState, SettingsState } from '../../settings.state';
import {
  ItemsWithouChildren,
  ItemWithChildrenPossible,
  ExtendedCustomDropdownItem,
  ExtendedCustomLinkItem,
  ExtendedSystemDropdownItem,
  ExtendedSystemLinkItem,
} from '../../../types';

type MenuItem =
  | ExtendedCustomDropdownItem
  | ExtendedCustomLinkItem
  | ExtendedSystemDropdownItem
  | ExtendedSystemLinkItem;

const setIds = (
  menuItem:
    | SystemLinkItem
    | SystemDropdownItem
    | CustomLinkItem
    | CustomDropdownItem,
): MenuItem => {
  if (
    menuItem.type === 'dropdown_item' ||
    menuItem.type === 'custom_dropdown_item'
  ) {
    return {
      ...menuItem,
      children: [
        ...menuItem.children.map((child) => setIds(child)),
        { type: 'link_item', id: 'error' } as ExtendedSystemLinkItem,
      ],
      id: nanoid(),
    };
  }

  return { ...menuItem, id: nanoid() };
};

export const updateSidebarMenuSettings = createAsyncThunk<
  void,
  ThunkValuesAndActions<UpdateSideMenuSettingsRequest>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateSidebarMenuSettings',
  async (
    { values, actions },
    { rejectWithValue, extra: { inject }, dispatch },
  ) => {
    try {
      const service = await inject(SideMenuService);

      await service.updateSideMenuSettings({ body: values });

      actions.resolve(null);

      dispatch(getSideMenu());
      dispatch(
        showNotification('Навигация в боковой панели успешно \nизменена'),
      );
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      dispatch(
        showError(
          'Не удалось изменить навигацию в боковой \nпанели!\nПопробуйте, пожалуйста, еще раз',
        ),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getSidebarMenuSettings = createAsyncThunk<
  GetSideMenuSettingsResponse,
  void,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getSidebarMenuSettings',
  async (_, { rejectWithValue, extra: { inject } }) => {
    try {
      const sideMenuService = await inject(SideMenuService);

      const result = await sideMenuService.getSideMenuSettings();

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsSidebarReducer = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(updateSidebarMenuSettings.pending, (state) => {
    state.companySidebar.status = 'loading';
  });
  builder.addCase(updateSidebarMenuSettings.fulfilled, (state) => {
    state.companySidebar.status = 'complete';
    state.companySidebar.needRefetch = true;
  });
  builder.addCase(updateSidebarMenuSettings.rejected, (state) => {
    state.companySidebar.status = 'complete';
    state.companySidebar.needRefetch = false;
  });
  builder.addCase(getSidebarMenuSettings.pending, (state) => {
    state.companySidebar.status = 'loading';
  });
  builder.addCase(getSidebarMenuSettings.fulfilled, (state, { payload }) => {
    state.companySidebar.status = 'complete';
    const result = payload.menu.map((item) => {
      if (
        item.type !== 'link_item' &&
        item.type !== 'custom_link_item' &&
        item.type !== 'user_section_item'
      ) {
        return {
          ...item,
          children: [
            ...item.children.map((child) => setIds(child)),
            { type: 'link_item', id: 'error' },
          ],
          id: nanoid(),
        } as ItemWithChildrenPossible;
      }

      return { ...item, id: nanoid() } as ItemsWithouChildren;
    });

    state.companySidebar.items = result;
    state.companySidebar.needRefetch = false;
  });
  builder.addCase(
    getSidebarMenuSettings.rejected,
    (state, { payload, error }) => {
      state.companySidebar = {
        status: 'failed',
        error:
          payload ||
          ({
            info: (error && error.message) || 'Unknown error',
            status: 500,
            source: 'client',
            title: 'Internal client error',
          } as AppError),
        items: [],
        needRefetch: false,
      };
    },
  );
};
